import { acceptHMRUpdate, defineStore } from 'pinia'

import AiSuggestionsService from '@/services/root/ai-suggestions'
import { TagType, UNALLOCATED_ALLOCATION_NAME } from '@/constants/tags'
import { HttpError } from '@/helpers/errors'
import { groupTagsByType, isParentAssetInfoTag } from '@/helpers/tags'

import type { AssetInfo } from '@/types/services/assets'
import type { Tag } from '@/types/services/tags'
import type {
  AiResuggestResult,
  AiSuggestion,
  AiSuggestionApplyItem,
  AiSuggestionsPendingEntity,
  CurrentAxisValues,
} from '@/types/services/ai-suggestions'
import { assetKeyOf } from '@/types/services/ai-suggestions'

/** An asset the classifier fails on never lands, so the wait needs an end. */
export const RESUGGEST_WAIT_MS = 30 * 60 * 1000

/** The asset's current values per axis, from its parent-scope tag links. */
export function currentAxisValues(
  assetInfoTags: AssetInfo['asset_info_tags'] | undefined,
  tagsMap: Record<string, Tag>
): CurrentAxisValues {
  // Child scope is not what the suggestions apply to.
  const parentLinks = (assetInfoTags ?? []).filter(isParentAssetInfoTag)
  const parentTags = parentLinks
    .map((link) => tagsMap[link.tag_id])
    .filter((tag): tag is Tag => tag !== undefined)
  const grouped = groupTagsByType(parentTags)
  const segment_tags = grouped[TagType.SEGMENT_TAG].map((tag) => tag.name)
  const category = grouped[TagType.CATEGORY_TAG][0]?.name ?? null
  segment_tags.sort((a, b) => a.localeCompare(b))
  const values: CurrentAxisValues = { segment_tags, category }
  // A link the vocabulary cannot resolve could be the allocation one (links
  // carry no type), so the allocation stays *unknown* rather than reading as
  // "unallocated". The confirm is add-only for every axis, so a wrong guess
  // could not overwrite anything -- but it would render a false diff whose
  // confirm silently changes nothing.
  if (parentTags.length === parentLinks.length) {
    values.allocation = grouped[TagType.ALLOCATION_TAG][0]?.name ?? null
  }
  return values
}

/**
 * True when the suggested allocation differs from the live current one.
 * Matching rows show "No change" and omit allocation from the apply
 * request. So does an unknown current value -- nothing to diff against.
 * Unallocated is the "Other" literal here, null on the backend.
 */
export function hasAllocationSuggestion(
  suggestion: AiSuggestion,
  currents: CurrentAxisValues
): boolean {
  if (suggestion.replaces_existing) {
    return true
  }
  if (currents.allocation === undefined) {
    return false
  }
  const current = currents.allocation ?? UNALLOCATED_ALLOCATION_NAME
  const suggested =
    suggestion.suggested_allocation ?? UNALLOCATED_ALLOCATION_NAME
  return suggested !== current
}

/**
 * True when the segment axis would change. A filled axis is settled: the
 * confirm is add-only and drops it (`apply.py::_confirm`).
 */
export function hasSegmentSuggestion(
  suggestion: AiSuggestion,
  currents: CurrentAxisValues
): boolean {
  if (suggestion.replaces_existing) {
    return true
  }
  return currents.segment_tags.length === 0
}

/** Category twin of `hasSegmentSuggestion`. */
export function hasCategorySuggestion(
  suggestion: AiSuggestion,
  currents: CurrentAxisValues
): boolean {
  if (suggestion.replaces_existing) {
    return true
  }
  return !currents.category
}

/**
 * True when any axis would change. Rows without one are hidden from the
 * table, yet still confirmed by the bulk apply. A re-suggested row always
 * qualifies -- the reviewer asked for the answer.
 */
export function hasAnyChange(
  suggestion: AiSuggestion,
  currents: CurrentAxisValues,
  withCategory: boolean
): boolean {
  return (
    hasAllocationSuggestion(suggestion, currents) ||
    hasSegmentSuggestion(suggestion, currents) ||
    (withCategory && hasCategorySuggestion(suggestion, currents))
  )
}

/** Per-row editable copy of the suggested values. */
interface SuggestionDraft {
  allocation: string // vocabulary name; UNALLOCATED_ALLOCATION_NAME = unallocated
  segment_tags: string[]
  // Single category name; null = "no category" (deliberate decline).
  category: string | null
}

function draftOf(suggestion: AiSuggestion): SuggestionDraft {
  return {
    // The backend serves "unallocated" as null.
    allocation: suggestion.suggested_allocation ?? UNALLOCATED_ALLOCATION_NAME,
    segment_tags: [...new Set(suggestion.suggested_segment_tags)],
    category: suggestion.suggested_category,
  }
}

interface AwaitedResuggest {
  assetIds: string[]
  deadline: number
}

interface AiSuggestionsState {
  entityId: string | null
  suggestions: { data: AiSuggestion[]; loading: boolean }
  drafts: Record<string, SuggestionDraft>
  // Monotonic token to drop stale overlapping fetches.
  fetchSeq: number
  // The queued re-suggest that has not landed yet; the badge polls while
  // it is set.
  awaitingResuggest: AwaitedResuggest | null
  pendingEntities: AiSuggestionsPendingEntity[]
}

export const useAiSuggestionsStore = defineStore('ai-suggestions', {
  state: (): AiSuggestionsState => ({
    entityId: null,
    suggestions: { data: [], loading: false },
    drafts: {},
    fetchSeq: 0,
    awaitingResuggest: null,
    pendingEntities: [],
  }),

  getters: {
    total(state): number {
      return state.suggestions.data.length
    },

    /**
     * Assets carrying an open re-suggested row -- the backend's own
     * `pending_replacements` skip rule, so it doubles as "has it landed".
     */
    replacementAssetIds(state): Set<string> {
      return new Set(
        state.suggestions.data
          .filter((s) => s.replaces_existing)
          .map(assetKeyOf)
          .filter(Boolean)
      )
    },

    /** True once every awaited asset has its re-suggested row. */
    resuggestArrived(): boolean {
      const awaiting = this.awaitingResuggest
      if (!awaiting) {
        return false
      }
      const landed = this.replacementAssetIds
      return awaiting.assetIds.every((id) => landed.has(id))
    },
  },

  actions: {
    /**
     * Load pending suggestions and seed the editable drafts. `refreshing`
     * is the background variant: no blanking, no loading flag, and drafts
     * survive -- a poll must not flicker the list or drop edits.
     */
    async fetch(entityId: string, refreshing = false) {
      const seq = ++this.fetchSeq
      this.entityId = entityId
      if (!refreshing) {
        this.suggestions = { data: [], loading: true }
      }
      try {
        const { data } = await AiSuggestionsService.getSuggestions(entityId)
        if (seq !== this.fetchSeq) {
          return
        }
        const rows = data.data
        const kept = refreshing ? this.drafts : {}
        this.suggestions = { data: rows, loading: false }
        this.drafts = Object.fromEntries(
          rows.map((s) => [s.id, kept[s.id] ?? draftOf(s)])
        )
      } catch (err) {
        if (seq === this.fetchSeq && !refreshing) {
          this.suggestions = { data: [], loading: false }
          this.drafts = {}
        }
        throw new HttpError(err)
      }
    },

    async fetchPendingEntities() {
      try {
        const response = await AiSuggestionsService.getPendingEntities()
        this.pendingEntities = response.data
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async resuggest(
      entityId: string,
      assetIds: string[]
    ): Promise<AiResuggestResult> {
      try {
        const response = await AiSuggestionsService.resuggest(
          entityId,
          assetIds
        )
        return response.data.data
      } catch (err) {
        throw new HttpError(err)
      }
    },

    /**
     * Start waiting for a queued re-suggest to land. The rows read here are
     * still the before picture: an asset that *already* carries one was
     * refused by the backend, so waiting on it would never end.
     */
    async trackResuggest(entityId: string, assetIds: string[]) {
      await this.fetch(entityId, true).catch(() => {})
      const skipped = this.replacementAssetIds
      const awaited = assetIds.filter((id) => !skipped.has(id))
      this.awaitingResuggest = awaited.length
        ? { assetIds: awaited, deadline: Date.now() + RESUGGEST_WAIT_MS }
        : null
    },

    /** Stop waiting -- landed, timed out, or the entity changed. */
    stopAwaitingResuggest() {
      this.awaitingResuggest = null
    },

    editAllocation(id: string, allocation: string) {
      if (this.drafts[id]) {
        this.drafts[id].allocation = allocation
      }
    },

    editSegmentTags(id: string, tags: string[]) {
      if (this.drafts[id]) {
        this.drafts[id].segment_tags = [...new Set(tags)]
      }
    },

    editCategory(id: string, category: string | null) {
      if (this.drafts[id]) {
        this.drafts[id].category = category
      }
    },

    /**
     * Bulk-confirm the rows the caller passes current values for, in one
     * request. Those values are what the diff is taken against, so a row
     * missing from the map is skipped rather than guessed at. The backend
     * applies the batch all-or-nothing, so returning without throwing means
     * every row went in.
     */
    async apply(currentsById: ReadonlyMap<string, CurrentAxisValues>) {
      const { entityId } = this
      if (!entityId || !this.suggestions.data.length) {
        return
      }
      const items: AiSuggestionApplyItem[] = []
      for (const s of this.suggestions.data) {
        const currents = currentsById.get(s.id)
        if (!currents) {
          continue
        }
        const draft = this.drafts[s.id]
        const item: AiSuggestionApplyItem = {
          id: s.id,
          status: 'confirmed',
        }
        // "No change" axes are omitted; the segment draft is always an
        // explicit decision (edits, or the seeded current tags).
        if (hasAllocationSuggestion(s, currents)) {
          // "Other" is a display label; the API unallocates on null.
          item.allocation =
            draft.allocation === UNALLOCATED_ALLOCATION_NAME
              ? null
              : draft.allocation
        }
        item.segment_tags = draft.segment_tags
        if (hasCategorySuggestion(s, currents)) {
          item.category = draft.category
        }
        items.push(item)
      }

      try {
        await AiSuggestionsService.applySuggestions(entityId, items)
      } catch (err) {
        throw new HttpError(err)
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useAiSuggestionsStore, import.meta.hot)
  )
}
